import type { CartItem as CartItemType } from "@workspace/crystallize/generated/shop/cart/";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { FormattedPrice } from "@workspace/ui/components/formatted-price";
import { Truck, X } from "lucide-react";
import QuantitySelector from "~/components/quantity-selector";
import { DiscountBadge } from "../discount-badge";
import CrystallizeResponsiveImage from "../product/crystallize-responsive-image";

type CartItemProps = {
	item: CartItemType;
	updateCart: (
		sku: string,
		quantity: number,
		action: "update" | "remove",
	) => void;
};

export function CartItem({ item, updateCart }: CartItemProps) {
	const compareAtPrice = item.variant.compareAtPrice;
	const hasCompareDiscount =
		compareAtPrice != null &&
		compareAtPrice.gross !== item.variant.price.gross;
	const discountPercent = hasCompareDiscount
		? Math.round((1 - item.variant.price.gross / compareAtPrice.gross) * 100)
		: 0;

	return (
		<Card key={item.variant.sku} className="overflow-hidden shadow-none">
			<CardContent className="p-4">
				<div className="flex gap-4">
					<div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-md">
						{item.variant.sku !== "freight" ? (
							<CrystallizeResponsiveImage
								image={item.variant.images?.[0] || { variants: [] }}
								className="aspect-square rounded-md bg-gray-200 object-cover"
								sizes="80px"
							/>
						) : (
							<div className="flex items-center justify-center">
								<Truck size={40} className="text-slate-500" />
							</div>
						)}
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex items-start justify-between">
							<div>
								<h3 className="font-medium text-lg">{item.name}</h3>
								<div className="flex items-center gap-1">
									{hasCompareDiscount && (
										<FormattedPrice
											price={compareAtPrice.gross}
											currency="NOK"
											variant="discount"
											className="text-slate-400 text-xs"
										/>
									)}
									<FormattedPrice
										price={item.variant.price.gross}
										currency="NOK"
										className="text-slate-500 text-sm"
									/>
								</div>
							</div>
							{item.variant.sku !== "freight" && (
								<button
									type="button"
									className="text-slate-400 hover:text-slate-600"
									onClick={() => {
										if (item.variant.sku) {
											updateCart(item.variant.sku, item.quantity, "remove");
										}
									}}
								>
									<X size={18} />
								</button>
							)}
						</div>

						<div className="mt-2 flex items-end justify-between">
							{item.variant.sku !== "freight" && (
								<QuantitySelector
									onQuantityChange={(value: number) => {
										const difference = value - item.quantity;
										const action = difference > 0 ? "update" : "remove";
										if (item.variant.sku) {
											updateCart(
												item.variant.sku,
												Math.abs(difference),
												action,
											);
										}
									}}
									defaultValue={item.quantity}
								/>
							)}

							{hasCompareDiscount ? (
								<div className="flex flex-col items-end gap-1">
									<div className="flex items-center gap-2">
										<FormattedPrice
											className="text-slate-500 text-sm"
											price={compareAtPrice.gross * item.quantity}
											variant="discount"
										/>
										<Badge variant="destructive" className="text-xs">
											-{discountPercent}%
										</Badge>
									</div>
									<FormattedPrice
										price={item.price.gross}
										variant="price"
										className="font-bold text-lg"
									/>
								</div>
							) : item.price.discounts && item.price.discounts.length > 0 ? (
								<div className="flex flex-col items-end gap-1">
									<div className="flex gap-2">
										<FormattedPrice
											className="text-slate-500 text-sm"
											price={item.variant.price.gross * item.quantity}
											variant="discount"
										/>
										<DiscountBadge discounts={item.price.discounts} />
									</div>
									<FormattedPrice
										price={item.price.gross}
										variant="price"
										className="font-bold text-lg"
									/>
								</div>
							) : (
								<FormattedPrice price={item.price.gross} variant="price" />
							)}
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
